import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

/**
 * Cadastro de nova empresa.
 *
 * O formulário não pede senha: ela é gerada pela API e enviada por e-mail.
 * Receber a mensagem é o que prova que o endereço é de quem se cadastrou —
 * sem isso, qualquer um abriria conta com o e-mail de outra pessoa.
 *
 * A validação de CNPJ roda aqui também, não porque se confie no navegador (a
 * API valida de novo), mas porque errar um dígito é comum e a resposta tem
 * que ser imediata.
 */

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3070';

interface Props {
  /** Plano escolhido no cartão de preços; vazio fecha o formulário. */
  plano: string | null;
  onFechar: () => void;
}

const NOME_DO_PLANO: Record<string, string> = {
  easy: 'Easy',
  starter: 'Starter',
  business: 'Business',
};

/** Dígitos verificadores do CNPJ. Mesma regra da API. */
function cnpjValido(valor: string): boolean {
  const n = valor.replace(/\D/g, '');
  if (n.length !== 14 || /^(\d)\1{13}$/.test(n)) return false;

  const digito = (base: string): number => {
    let peso = base.length - 7;
    let soma = 0;
    for (let i = 0; i < base.length; i++) {
      soma += Number(base[i]) * peso--;
      if (peso < 2) peso = 9;
    }
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  return digito(n.slice(0, 12)) === Number(n[12]) && digito(n.slice(0, 13)) === Number(n[13]);
}

const mascaraCnpj = (v: string): string =>
  v
    .replace(/\D/g, '')
    .slice(0, 14)
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');

export function Signup({ plano, onFechar }: Props): ReactNode {
  const [company, setCompany] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);
  const primeiro = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (plano) primeiro.current?.focus();
  }, [plano]);

  useEffect(() => {
    const fechaComEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onFechar();
    };
    document.addEventListener('keydown', fechaComEsc);
    return () => document.removeEventListener('keydown', fechaComEsc);
  }, [onFechar]);

  if (!plano) return null;

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErro(null);

    if (!cnpjValido(cnpj)) {
      setErro('CNPJ inválido. Confira os números.');
      return;
    }

    setEnviando(true);
    try {
      const resposta = await fetch(`${API_URL}/v1/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company, cnpj, name, email, plan: plano }),
      });

      if (resposta.ok) {
        setPronto(true);
        return;
      }

      // A API responde problem+json: `detail` já é uma frase para a pessoa.
      const corpo = (await resposta.json().catch(() => ({}))) as { detail?: string; errors?: unknown };
      setErro(
        corpo.detail ??
          (resposta.status === 429
            ? 'Muitos cadastros deste endereço. Tente de novo mais tarde.'
            : 'Não foi possível concluir o cadastro. Tente de novo em instantes.'),
      );
    } catch {
      setErro('Não foi possível falar com o servidor. Verifique a conexão e tente de novo.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="modal-fundo" onClick={onFechar}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Criar conta"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-fechar" onClick={onFechar} aria-label="Fechar">
          ×
        </button>

        {pronto ? (
          <>
            <h3>Conta criada.</h3>
            <p className="modal-sub">
              Enviamos a senha de acesso para <b>{email}</b>. Se não aparecer em alguns minutos, confira o
              spam.
            </p>
            <p className="modal-sub">
              Para emitir a primeira nota você vai precisar do CNPJ do emitente e do certificado digital A1 —
              os dois se cadastram no portal.
            </p>
            <button className="btn btn-primary" onClick={onFechar} style={{ width: '100%' }}>
              Fechar
            </button>
          </>
        ) : (
          <form onSubmit={enviar}>
            <h3>Criar conta · {NOME_DO_PLANO[plano] ?? plano}</h3>
            <p className="modal-sub">
              A conta começa em teste, sem cobrança. A senha de acesso vai para o seu e-mail.
            </p>

            <label>
              Razão social
              <input
                ref={primeiro}
                required
                minLength={2}
                maxLength={120}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Empresa Ltda"
              />
            </label>

            <label>
              CNPJ
              <input
                required
                inputMode="numeric"
                value={cnpj}
                onChange={(e) => setCnpj(mascaraCnpj(e.target.value))}
                placeholder="00.000.000/0000-00"
              />
            </label>

            <label>
              Seu nome
              <input
                required
                minLength={2}
                maxLength={80}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome e sobrenome"
              />
            </label>

            <label>
              E-mail
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@empresa.com.br"
              />
            </label>

            {erro && (
              <p className="modal-erro" role="alert">
                {erro}
              </p>
            )}

            <button className="btn btn-primary" type="submit" disabled={enviando} style={{ width: '100%' }}>
              {enviando ? 'Criando...' : 'Criar conta'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
